/**
 * ExitSwitch — the switch half of the switch→gate pair (Brief 014). N++'s exit switch,
 * Mario's P-switch, Hollow-Knight's lever — the trigger that opens a gated exit.
 *
 * A non-solid OVERLAP entity. On touch it sets the World latch keyed `props.linkId`
 * (via ctx.setLatch) — every EXITGATE sharing that linkId then unlocks. The latch is
 * World-owned and cleared on respawn, so a death re-locks the gate (replay-safe).
 *
 * Determinism: a single boolean latch write through ctx, no math → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

export class ExitSwitch implements Entity {
  readonly type = EntityType.EXITSWITCH;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private linkId: number;
  private on = false;

  /** @param props `linkId` (int; pairs with EXITGATE(s) sharing it; default 1). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const raw = props?.linkId;
    const id = typeof raw === 'number' ? raw : Number(raw ?? 1);
    this.linkId = Number.isFinite(id) ? Math.floor(id) : 1;
  }

  update(_dt: number, ctx: EntityContext): void {
    // Reflect the shared latch (another switch on the same id may have flipped it).
    this.on = ctx.latchOn(this.linkId);
  }

  onPlayerOverlap(ctx: EntityContext): void {
    ctx.setLatch(this.linkId, true); // idempotent; World jingles only on a real flip
    this.on = true;
  }

  spriteKey(): string { return this.on ? 'exitswitch_on' : 'exitswitch'; }

  visible(): boolean { return true; }
}
